import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PALETTE, matte, colored, vary, seededRandom, jitter, Fader } from '../../Style.js';

// Obstacles keep the exact footprint the server collides against, but are
// dressed as hand-built scenery: coursed stone walls, braziered columns,
// crates and palisades, and plank-decked earthworks.
export class Obstacle {
  constructor(scene, obstacleData) {
    this.scene = scene;
    this.data = obstacleData;
    this.mesh = null;
    this.fader = null;
    this.flames = [];
    this.rand = seededRandom(hashId(obstacleData.id));
    this.create();
  }

  create() {
    const { type, position, size, rotation } = this.data;

    this.mesh = new THREE.Group();

    switch (type) {
      case 'wall':
        this.buildWall(size);
        break;
      case 'pillar':
        this.buildPillar(size);
        break;
      case 'cover':
        this.buildCover(size);
        break;
      case 'ramp':
        // Zero elevations are omitted from the server's JSON
        this.buildRamp(size, this.data.elevationStart ?? 0, this.data.elevationEnd ?? 0);
        break;
      default:
        this.addMerged([colored(new THREE.BoxGeometry(size.x, size.y, size.z), PALETTE.stone, { y: size.y / 2 })]);
    }

    // Ramps are built from their footprint origin; everything else from its base
    this.mesh.position.set(position.x, position.y, position.z);

    if (rotation) {
      this.mesh.rotation.y = rotation;
    }

    this.scene.add(this.mesh);
    this.mesh.updateMatrixWorld(true);

    // Ramps are walked on, so they never fade out of the way
    if (type !== 'ramp') {
      this.fader = new Fader(this.mesh);
    }
  }

  addMerged(geometries) {
    const mesh = new THREE.Mesh(mergeGeometries(geometries), matte(0xffffff, { vertexColors: true }));
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.mesh.add(mesh);
    return mesh;
  }

  // Coursed stone blocks along the wall's long axis, with a capping course.
  buildWall(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = alongX ? size.z : size.x;
    const parts = [];

    const courseHeight = 0.75;
    const courses = Math.max(1, Math.round((size.y - 0.4) / courseHeight));
    const h = (size.y - 0.4) / courses;

    for (let row = 0; row < courses; row++) {
      let cursor = -length / 2;
      // Stagger the joints on alternate courses
      let first = row % 2 === 0 ? 0.6 + rand() * 0.6 : 1.2 + rand() * 0.8;
      while (cursor < length / 2 - 0.01) {
        const blockLength = Math.min(first, length / 2 - cursor);
        first = 1.1 + rand() * 1.2;
        const inset = rand() * 0.08;
        const color = rand() < 0.12 ? vary(PALETTE.mossStone, rand) : vary(PALETTE.stone, rand, 0.14, 0.02);
        const geo = new THREE.BoxGeometry(
          Math.max(0.05, blockLength - 0.06),
          h - 0.05,
          thickness - inset
        );
        const center = cursor + blockLength / 2;
        parts.push(colored(geo, color, {
          x: alongX ? center : 0,
          z: alongX ? 0 : center,
          y: row * h + h / 2,
          ry: alongX ? 0 : Math.PI / 2
        }));
        cursor += blockLength;
      }
    }

    // Mortar core so there are no see-through gaps between blocks
    parts.push(colored(
      new THREE.BoxGeometry(alongX ? length - 0.04 : thickness * 0.8, size.y - 0.4, alongX ? thickness * 0.8 : length - 0.04),
      PALETTE.stoneDark,
      { y: (size.y - 0.4) / 2 }
    ));

    // Capping slabs, slightly overhanging
    let cursor = -length / 2;
    while (cursor < length / 2 - 0.01) {
      const slab = Math.min(1.6 + rand() * 1.2, length / 2 - cursor);
      const center = cursor + slab / 2;
      parts.push(colored(
        new THREE.BoxGeometry(slab - 0.04, 0.4, thickness + 0.3),
        vary(PALETTE.stoneLight, rand, 0.1),
        { x: alongX ? center : 0, z: alongX ? 0 : center, y: size.y - 0.2, ry: alongX ? 0 : Math.PI / 2 }
      ));
      cursor += slab;
    }

    // A few tufts of ivy for life
    const ivyCount = Math.floor(length / 8);
    for (let i = 0; i < ivyCount; i++) {
      const along = (rand() - 0.5) * (length - 2);
      const side = rand() < 0.5 ? -1 : 1;
      const ivyHeight = 1 + rand() * (size.y - 1.5);
      const offset = side * (thickness / 2 + 0.05);
      parts.push(colored(
        jitter(new THREE.IcosahedronGeometry(0.6 + rand() * 0.5, 0), 0.25, rand),
        vary(PALETTE.foliage, rand, 0.12),
        {
          x: alongX ? along : offset,
          z: alongX ? offset : along,
          y: ivyHeight,
          scale: [1, 1.3, 0.35],
          ry: alongX ? 0 : Math.PI / 2
        }
      ));
    }

    this.addMerged(parts);
  }

  // A stone column on a plinth, crowned with a burning brazier.
  buildPillar(size) {
    const rand = this.rand;
    const radius = size.x / 2;
    const parts = [];

    parts.push(colored(new THREE.CylinderGeometry(radius * 1.25, radius * 1.35, 0.6, 8), vary(PALETTE.stoneDark, rand), { y: 0.3 }));

    const shaftHeight = size.y - 1.4;
    const drums = Math.max(2, Math.round(shaftHeight / 1.2));
    const drumHeight = shaftHeight / drums;
    for (let i = 0; i < drums; i++) {
      const drum = jitter(new THREE.CylinderGeometry(radius * 0.95, radius, drumHeight - 0.06, 8), 0.08, rand);
      parts.push(colored(drum, vary(PALETTE.stone, rand, 0.12), { y: 0.6 + i * drumHeight + drumHeight / 2, ry: rand() }));
    }

    parts.push(colored(new THREE.CylinderGeometry(radius * 1.3, radius * 1.05, 0.5, 8), vary(PALETTE.stoneLight, rand), { y: size.y - 0.55 }));
    // Iron brazier bowl
    parts.push(colored(new THREE.CylinderGeometry(radius * 0.9, radius * 0.5, 0.5, 8), PALETTE.iron, { y: size.y - 0.05 }));
    parts.push(colored(new THREE.CylinderGeometry(radius * 0.75, radius * 0.75, 0.12, 8), 0x2a1a10, { y: size.y + 0.15 }));

    this.addMerged(parts);

    // Flame: two nested emissive cones that flicker
    const flame = new THREE.Group();
    const outer = new THREE.Mesh(
      new THREE.ConeGeometry(radius * 0.55, 1.4, 6),
      new THREE.MeshBasicMaterial({ color: 0xff8a2a, transparent: true, opacity: 0.85 })
    );
    const inner = new THREE.Mesh(
      new THREE.ConeGeometry(radius * 0.3, 0.9, 5),
      new THREE.MeshBasicMaterial({ color: 0xffe28a })
    );
    outer.position.y = 0.7;
    inner.position.y = 0.45;
    // Fire is light, not a surface: it never fades or casts shadows
    outer.userData.noFade = true;
    inner.userData.noFade = true;
    flame.add(outer, inner);
    flame.position.y = size.y + 0.2;
    flame.userData.phase = rand() * Math.PI * 2;
    this.mesh.add(flame);
    this.flames.push(flame);
  }

  // Thin barriers become a palisade; squat blocks become stacked crates.
  buildCover(size) {
    const rand = this.rand;
    const parts = [];
    const longest = Math.max(size.x, size.z);
    const shortest = Math.min(size.x, size.z);

    if (longest / shortest > 3) {
      const alongX = size.x >= size.z;
      const stakes = Math.max(2, Math.round(longest / 0.55));
      const spacing = longest / stakes;
      for (let i = 0; i < stakes; i++) {
        const along = -longest / 2 + spacing * (i + 0.5);
        const stakeHeight = size.y * (0.9 + rand() * 0.2);
        const stake = new THREE.CylinderGeometry(spacing * 0.45, spacing * 0.5, stakeHeight, 6);
        parts.push(colored(stake, vary(PALETTE.wood, rand, 0.12), {
          x: alongX ? along : 0,
          z: alongX ? 0 : along,
          y: stakeHeight / 2
        }));
        parts.push(colored(new THREE.ConeGeometry(spacing * 0.45, 0.35, 6), vary(PALETTE.woodLight, rand), {
          x: alongX ? along : 0,
          z: alongX ? 0 : along,
          y: stakeHeight + 0.17
        }));
      }
      // Lashed cross-rails on both faces
      for (const side of [-1, 1]) {
        for (const railY of [size.y * 0.3, size.y * 0.7]) {
          parts.push(colored(new THREE.BoxGeometry(longest, 0.14, 0.14), PALETTE.woodDark, {
            x: alongX ? 0 : side * shortest * 0.5,
            z: alongX ? side * shortest * 0.5 : 0,
            y: railY,
            ry: alongX ? 0 : Math.PI / 2
          }));
        }
      }
    } else {
      // Big crate filling the footprint, with smaller crates and a barrel stacked on top
      const crate = (w, h, d, x, y, z, ry) => {
        const color = vary(PALETTE.woodLight, rand, 0.12);
        parts.push(colored(new THREE.BoxGeometry(w, h, d), color, { x, y: y + h / 2, z, ry }));
        // Dark banding on the edges
        parts.push(colored(new THREE.BoxGeometry(w + 0.04, 0.16, d + 0.04), PALETTE.woodDark, { x, y: y + 0.12, z, ry }));
        parts.push(colored(new THREE.BoxGeometry(w + 0.04, 0.16, d + 0.04), PALETTE.woodDark, { x, y: y + h - 0.12, z, ry }));
        parts.push(colored(new THREE.BoxGeometry(w * 0.16, h, d + 0.04), PALETTE.woodDark, { x, y: y + h / 2, z, ry }));
      };

      const baseHeight = size.y * 0.6;
      crate(size.x, baseHeight, size.z, 0, 0, 0, 0);
      const topHeight = size.y - baseHeight;
      crate(size.x * 0.55, topHeight, size.z * 0.55, -size.x * 0.18, baseHeight, -size.z * 0.15, (rand() - 0.5) * 0.4);

      const barrelRadius = Math.min(size.x, size.z) * 0.16;
      parts.push(colored(new THREE.CylinderGeometry(barrelRadius, barrelRadius * 1.1, topHeight * 0.9, 8), vary(PALETTE.wood, rand), {
        x: size.x * 0.28, y: baseHeight + topHeight * 0.45, z: size.z * 0.25
      }));
      // Sack slumped against the side
      parts.push(colored(jitter(new THREE.IcosahedronGeometry(0.5, 0), 0.2, rand), PALETTE.cloth, {
        x: size.x / 2 + 0.2, y: 0.4, z: size.z * 0.2, scale: [1, 0.8, 1.1]
      }));
    }

    this.addMerged(parts);
  }

  // Earthwork with stone sides and plank decking that follows the slope.
  buildRamp(size, elevStart, elevEnd) {
    const rand = this.rand;
    const body = new THREE.Mesh(
      this.createRampGeometry(size, elevStart, elevEnd),
      matte(PALETTE.stoneDark)
    );
    body.castShadow = true;
    body.receiveShadow = true;
    this.mesh.add(body);

    const rise = elevEnd - elevStart;
    const slope = Math.atan2(rise, size.z);
    const slopeLength = Math.hypot(size.z, rise);
    const boards = Math.max(2, Math.round(slopeLength / 0.9));
    const boardDepth = slopeLength / boards;
    const parts = [];
    for (let i = 0; i < boards; i++) {
      const t = (i + 0.5) / boards;
      const z = -size.z / 2 + t * size.z;
      const y = elevStart + t * rise + 0.08;
      parts.push(colored(new THREE.BoxGeometry(size.x - 0.2, 0.16, boardDepth - 0.06), vary(PALETTE.woodLight, rand, 0.14), {
        y, z, rx: -slope
      }));
    }

    // Stone kerbs along both edges
    for (const side of [-1, 1]) {
      const kerb = new THREE.BoxGeometry(0.5, 0.4, slopeLength);
      parts.push(colored(kerb, vary(PALETTE.stone, rand), {
        x: side * (size.x / 2 - 0.25),
        y: (elevStart + elevEnd) / 2 + 0.2,
        rx: -slope
      }));
    }

    this.addMerged(parts);
  }

  createRampGeometry(size, elevStart, elevEnd) {
    // Create a ramp using BufferGeometry
    // The ramp goes from elevStart at -Z to elevEnd at +Z
    const halfX = size.x / 2;
    const halfZ = size.z / 2;

    // Define vertices for the ramp shape
    const vertices = new Float32Array([
      // Bottom face (y = 0)
      -halfX, 0, -halfZ,
       halfX, 0, -halfZ,
       halfX, 0,  halfZ,
      -halfX, 0,  halfZ,

      // Top face (sloped)
      -halfX, elevStart, -halfZ,
       halfX, elevStart, -halfZ,
       halfX, elevEnd,    halfZ,
      -halfX, elevEnd,    halfZ,
    ]);

    // Define faces using indices
    const indices = [
      // Bottom
      0, 1, 2,  0, 2, 3,
      // Top (sloped surface)
      4, 6, 5,  4, 7, 6,
      // Front (low end)
      0, 4, 5,  0, 5, 1,
      // Back (high end)
      2, 6, 7,  2, 7, 3,
      // Left side
      0, 3, 7,  0, 7, 4,
      // Right side
      1, 5, 6,  1, 6, 2,
    ];

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  // Fade towards the given opacity (1 = solid) when blocking the camera's view.
  setFadeTarget(opacity) {
    if (this.fader) this.fader.target = opacity;
  }

  update(deltaTime, elapsed) {
    if (this.fader) this.fader.update(deltaTime);
    for (const flame of this.flames) {
      const p = flame.userData.phase;
      flame.scale.set(
        1 + Math.sin(elapsed * 9 + p) * 0.08,
        1 + Math.sin(elapsed * 13 + p * 2) * 0.18 + Math.sin(elapsed * 5.3 + p) * 0.08,
        1 + Math.cos(elapsed * 8 + p) * 0.08
      );
      flame.rotation.y = elapsed * 1.5 + p;
    }
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.traverse(child => {
        if (child.isMesh) child.geometry.dispose();
      });
      if (this.fader) this.fader.dispose();
      this.mesh = null;
    }
  }
}

function hashId(id) {
  let h = 2166136261;
  for (const ch of String(id)) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
